import { BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, useBCMSContext, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import Classes, { ACHTemplate, ACHToParticipant, Company, CompanyAccount, NonNachaParticipants, Participant } from "model";
import React from "react";

import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from "components/shared";
import { PrimaryButton, SecondaryButton, UITextInput } from "components/ui";
import { SVGAsset } from "res";
import ImportFileAdditionalValuesWidget from "./components/ImportFileAdditionalValuesWidget";
import ImportFileExcludeWidget from "./components/ImportFileExcludeWidget";
import ImportFileFormatWidget from "./components/ImportFileFormatWidget";
import ImportFileMapColumnsWidget from "./components/ImportFileMapColumnsWidget";
import ImportFileMappingPreviewWidget from "./components/ImportFileMappingPreviewWidget";
import ImportFilePreviewWidget from "./components/ImportFilePreviewWidget";

import "./styles.css";
import "./mobile.css";


/**
 * @function ImportFileNewTemplatePage
 * @return {React.Component}
 * @exported true
 * @description The main component for the ImportFileNewTemplatePage.
 */
export default function ImportFileNewTemplatePage(props) {
    const { prevStep, nextStep } = useWorkflow() ?? {};
    const context = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, context);


    const data = { ...getDefaultData(), ...props.data ?? {} };
    const ui = { ...getDefaultUI(), ...props.ui ?? {} };


    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,
        onChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
                .update(["data", "template", prop], value)
                .submit(setState);
        },
        onDataChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
                .update(["data", prop], value)
                .submit(setState);
        },
        onColumnChange: (id) => value => {

            const selectedColumns = data.selectedColumns.reduce((acc, item) => {
                if (item.id === id) {
                    item.value = value;
                    item.value.isSelected = true;
                }
                return [...acc, item]
            }, []);

            const mapColumns = data.mapColumns.reduce((acc, item) => {
                const selected = selectedColumns.find(column => column.value.id === item.id);

                if (item.isSelected !== undefined) {
                    item.isSelected = selected !== undefined ? true : false;
                }

                return [...acc, item]
            }, []);

            BCMSStateManagerCRUD.init(state)
                .update(["data", "selectedColumns"], selectedColumns)
                .update(["data", "mapColumns"], mapColumns)
                .submit(setState);
        },
        onCancel: () => {
            prevStep();
            window.scrollTo(0, 0);
        },
        onContinue: () => {
            const defaultTemplate = data.defaultTemplate;
            const mergedTemplate = BCMSDefaultModel.mergeRecords(data.template.record, defaultTemplate)
            const updateTemplate = ACHTemplate.init({ ...mergedTemplate });

            BCMSStateManagerCRUD.init(state)
                .update(["data", "template"], updateTemplate)
                .submit(setState);
                
            nextStep({ data, ui });
            window.scrollTo(0, 0);
        }
    });
    return (
        <PageContainer className="import-file-new-template-page" state={{ data, ui }} controller={controller}>
            <ImportFileNewTemplatePageContent />
        </PageContainer>
    )
}

export const getDefaultData = (participants, achToParticipants) => {

    let nextParticipantId = Participant.nextID();
    const nonNachaParticipants = NonNachaParticipants.initAll().reduce((acc, item) => {
        const added = Participant.init({
            ...item.record,
            id: nextParticipantId,
        });

        nextParticipantId = parseFloat(nextParticipantId) + 1
        acc.push(added);

        return acc;
    }, []);

    let nextACHToParticipantId = ACHToParticipant.nextID();
    const achToParticipant = nonNachaParticipants.reduce((acc, item) => {
        const added = ACHToParticipant.init({
            id: nextACHToParticipantId,
            participant: Participant.init({ ...item.record, isSelected: "true" }),
            notify: "No",
            addenda: "0",
            numberOfDistributions: "1",
            amount: 1
        });

        nextACHToParticipantId = parseFloat(nextACHToParticipantId) + 1
        acc.push(added);

        return acc;
    }, []);

    const _achToParticipants = achToParticipants ?? achToParticipant;

    return {
        selectedColumns: [
            { id: "0", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
            { id: "1", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
            { id: "2", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
            { id: "3", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
            { id: "4", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
        ],
        mapColumns: [
            { id: "0", value: "(Exclude Column)", class: "placeholder-item" },
            { id: "1", value: "Name (Required)", class: "name", isSelected: false },
            { id: "2", value: "Amount (Required)", class: "amount", isSelected: false },
            { id: "3", value: "Account Number (Required)", class: "account-number", isSelected: false },
            { id: "4", value: "Transaction Code", class: "transaction-code", isSelected: false },
            { id: "5", value: "Payment Information", class: "payment-information", isSelected: false },
            { id: "6", value: "Identification", class: "identification", isSelected: false },
            { id: "7", value: "Routing Transit (Required)", class: "routing-transit", isSelected: false },
            { id: "8", value: "Effective Date", class: "effective-date", isSelected: false },
        ],
        achToParticipants: {
            participants: achToParticipants ?? _achToParticipants
        },
        participants: participants ?? Participant.initAll(),
        template: ACHTemplate.init({
            id: ACHTemplate.nextID(),
            transactionType: "payment",
            secCode: "PPD Credit - Consumer Credit",
            name: "",
            companyDiscretionaryData: "",
            companyEntryDescription: "",
            creditAmount: "0.00",
            debitAmount: "0.00",
            achToParticipants: [],
            achFromAccount: [],
            company: Company.initByID(0),
            companyAccount: CompanyAccount.initByID(0),
            numberOfAccounts: "0"
        }),
        defaultTemplate:
            ACHTemplate.init({
                id: ACHTemplate.nextID(),
                transactionType: "payment",
                secCode: "PPD Credit - Consumer Credit",
                name: "SamplePPD",
                companyDiscretionaryData: "",
                companyEntryDescription: "",
                creditAmount: "0.00",
                debitAmount: "0.00",
                achToParticipants: [],
                achFromAccount: [],
                company: Company.initByID(0),
                companyAccount: CompanyAccount.initByID(0),
                numberOfAccounts: "0"
            }),
        numberOfHeaderRows: "",
        numberOfFooterRows: "",
        inputDecimalsIntoAmounts: "No",
        textQualifier: "None",
        fileFormat: "Comma",
        chosenFileName: "SamplePPD.csv",
    };
};

export const getDefaultUI = () => ({
    mapColumnSelect1: { isActive: false },
    mapColumnSelect2: { isActive: false },
    mapColumnSelect3: { isActive: false },
    mapColumnSelect4: { isActive: false },
    mapColumnSelect5: { isActive: false }
});


/**
 * @function ImportFileNewTemplatePageContent
 * @return {React.Component}
 * @exported true
 * @description The page content component for the ImportFileNewTemplatePage.
 */
function ImportFileNewTemplatePageContent(props) {
    const { ui, data, onChange, onCancel, onContinue } = useStateManager();
    const importTemplate = data.template;
    const templateName = importTemplate.name;
    const company = importTemplate.companyAccount;
    const transactionType = importTemplate.secCode;

    return (
        <PageContentView>
            <CenterContentContainer>
                <ContentHeaderView text="Import File - New Template" />
                <FormContainer className="small-form-width">
                    <FormRowView text="Company" className="company-row">
                        <b>{company.name} - {company.number}</b>
                    </FormRowView>
                    <FormRowView text="Template Name">
                        <UITextInput dataPoint={ `Template_Name_Input` } value={templateName} onChange={onChange("name")} />
                    </FormRowView>
                    <FormRowView text="Transaction Type" className="transaction-type-row">
                        <b>{transactionType}</b>
                    </FormRowView>
                    <FormRowView className="input-decimals-into-amounts-row" text={
                        <div className="flex-container flex-horizontal align-center">
                            <p className="label-with-icon">Input Decimals into Amount</p>
                            <SVGAsset id="InfoIcon" />
                        </div>
                    }>
                        <DisabledPrompt>
                            <CustomSelect dataPoint={ `Input_Decimals_Into_Amounts_Select` } value="no">
                                <CustomSelectItem dataPoint={ `Input_Decimals_Into_Amounts_Option_${ `Yes` }` } value="yes">Yes</CustomSelectItem>
                                <CustomSelectItem dataPoint={ `Input_Decimals_Into_Amounts_Option_${ `No` }` } value="no">No</CustomSelectItem>
                            </CustomSelect>
                        </DisabledPrompt>
                    </FormRowView>
                    <ImportFileExcludeWidget />
                </FormContainer>
                <ImportFileFormatWidget />
                <ImportFilePreviewWidget />
                <ImportFileMapColumnsWidget />
                <ImportFileMappingPreviewWidget />
                <ImportFileAdditionalValuesWidget />

                <div className="button-container flex-container flex-horizontal">
                    <SecondaryButton dataPoint={ `Cancel_Button` } text="Cancel" onClick={onCancel} />
                    <PrimaryButton dataPoint={ `Continue_Button` } text="Continue" onClick={onContinue} />
                </div>
            </CenterContentContainer>
        </PageContentView>
    )
}